// Çağıranın etkin saat dilimi (SEM-6938).
//
// NEDEN SUNUCUDAN: sunucu haftalık puantajı kiracının dilimindeki gün sınırına
// göre kuruyor (`jobs/timesheet-assembly-sweep.ts`). Ekran damgaları tarayıcı
// diliminde çizerse gösterilen saat ile gruplandığı gün ayrı dilimlerden gelir —
// Londra'daki bir çalışan 08:00 damgasını 05:00 görür ama satır hâlâ İstanbul
// gününün altındadır.
//
// Kendi sorgu anahtarında yaşıyor, `use-company-context` ile aynı sebeple:
// /v1/me'yi kendi dilimine daraltan her hook cevabı o daraltmayla önbelleğe alır.
//
// Geri düşüş tarayıcı dilimidir, UTC değil. Cevap gelmeden bir ekran çizilirse
// kullanıcının kendi saatini göstermek, herkese UTC göstermekten daha az yanlış.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { platformGraph } from "@/api/platform-graph";

export const TIME_ZONE_QUERY_KEY = ["me", "time-zone"] as const;

type MeGet = (
  path: "/v1/me",
) => Promise<
  EnvelopedFetchResult<{ time_zone: string | null; effective_time_zone: string }>
>;

/** Tarayıcının IANA dilimi; Intl'in erişilemediği ortamda UTC. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

interface TimeZones {
  /** Kişinin KENDİ seçimi; null = seçim yok, kiracıyı takip et. */
  readonly own: string | null;
  /** `çalışan ?? kiracı ?? UTC` zincirinin sunucudaki cevabı. */
  readonly effective: string;
}

async function fetchTimeZones(): Promise<TimeZones> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { own: data.time_zone, effective: data.effective_time_zone };
}

export function useTimeZone(): string {
  const query = useQuery({
    queryKey: TIME_ZONE_QUERY_KEY,
    queryFn: fetchTimeZones,
    staleTime: 60_000,
  });
  return query.data?.effective ?? browserTimeZone();
}

export interface TimeZonePreference {
  readonly own: string | null;
  /**
   * Sunucunun çözdüğü etkin dilim; cevap inmeden önce null.
   *
   * `useTimeZone`in tarayıcı geri düşüşü BURADA yok ve olmamalı: seçici bu
   * değeri "şirket varsayılanı (X)" diye YAZIYOR. Geri düşüş, kiracısı
   * Europe/Istanbul olan kullanıcıya bir an "şirket varsayılanı
   * (Europe/Berlin)" gösterirdi — damga çizen ekranda en az yanlış olan
   * tahmin, etiket yazan ekranda düpedüz yanlış bir iddiadır.
   */
  readonly effective: string | null;
  readonly isLoading: boolean;
  /** null GEÇMEK seçimi siler ve kullanıcıyı kiracı varsayılanına döndürür. */
  readonly setTimeZone: (next: string | null) => void;
  readonly isSaving: boolean;
  readonly saveError: unknown;
}

/**
 * Saat dilimi seçicisinin bağlantısı (SEM-6939).
 *
 * Dil tercihinin aksine YEREL bir kopya YOK: `useLocalePreference` seçimi önce
 * i18next'e uygular çünkü dil istemcide yaşar, sunucu yazımı yalnız bildirim
 * kopyasını ilgilendirir. Etkin dilimi ise ekranlar sunucudan okur
 * (`useTimeZone`); istemcide ikinci bir kopya tutmak, damgaları kaydedilmemiş
 * bir dilimde çizerdi. Bu yüzden yazım beklenir ve sonra sorgu geçersizlenir.
 */
export function useTimeZonePreference(): TimeZonePreference {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: TIME_ZONE_QUERY_KEY,
    queryFn: fetchTimeZones,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (next: string | null) =>
      unwrapEnvelope(
        platformGraph.PUT("/v1/me/timezone", { body: { time_zone: next } }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TIME_ZONE_QUERY_KEY }),
  });

  return {
    own: query.data?.own ?? null,
    effective: query.data?.effective ?? null,
    isLoading: query.isLoading,
    setTimeZone: (next) => {
      mutation.mutate(next);
    },
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
